import { useCallback, useEffect, useState } from 'react';
import SuperAdminPanel from './SuperAdminPanel';
import AdmissionsAdminPanel from './AdmissionsAdminPanel';
import CmsWorkflowPanel from './CmsWorkflowPanel';
import AdmissionsPortal from './AdmissionsPortal';

async function apiRequest(apiBase, path, options = {}) {
  try {
    console.log(`[API] Requesting: ${apiBase}${path}`, options);
    const response = await fetch(`${apiBase}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    });

    console.log(`[API] Response status: ${response.status} for ${path}`);
    const payload = await response.json().catch((err) => {
      console.error(`[API] Failed to parse JSON:`, err);
      return {};
    });
    
    console.log(`[API] Response payload:`, payload);
    
    if (!response.ok || !payload.ok) {
      console.error(`[API] Request failed - Status: ${response.status}, ok: ${payload.ok}, message: ${payload.message}`);
      throw new Error(payload.message || `Request failed with status ${response.status}`);
    }
    return payload;
  } catch (error) {
    console.error(`[API] Error in apiRequest:`, error);
    throw error;
  }
}

function UnifiedAccessPortal({ apiBase }) {
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState('none');
  const [staffUser, setStaffUser] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [showParentRegister, setShowParentRegister] = useState(false);
  const [parentForm, setParentForm] = useState({
    full_name: '',
    email: '',
    password: '',
  });

  const resolveStaffContext = useCallback((user) => {
    const roles = new Set(user?.roles || []);
    if (roles.has('super_admin')) {
      return 'super_admin';
    }
    if (roles.has('admissions')) {
      return 'admissions_admin';
    }
    return 'cms_admin';
  }, []);

  const detectExistingSession = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const staff = await apiRequest(apiBase, '/auth/me');
      const user = staff.data?.user;
      setStaffUser(user || null);
      setContext(resolveStaffContext(user));
      setLoading(false);
      return;
    } catch (_) {
      // Not a staff session.
    }

    try {
      await apiRequest(apiBase, '/admissions/me');
      setContext('parent');
    } catch (_) {
      setContext('none');
    } finally {
      setLoading(false);
    }
  }, [apiBase, resolveStaffContext]);

  useEffect(() => {
    detectExistingSession();
  }, [detectExistingSession]);

  async function handleUnifiedLogin(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      console.log('Attempting staff login with:', { email, apiBase });
      const staff = await apiRequest(apiBase, '/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      console.log('Staff login successful:', staff);
      const user = staff.data?.user;
      setStaffUser(user || null);
      setContext(resolveStaffContext(user));
      setPassword('');
      setSuccess('Login berhasil. Anda masuk sebagai staff.');
      return;
    } catch (staffError) {
      console.error('Staff login failed:', staffError.message);
      // Fallback to parent admissions login.
    }

    try {
      console.log('Attempting parent admissions login with:', { email });
      await apiRequest(apiBase, '/admissions/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      console.log('Parent admissions login successful');
      setContext('parent');
      setPassword('');
      setSuccess('Login berhasil. Anda masuk sebagai orang tua.');
    } catch (err) {
      console.error('Both login attempts failed:', err.message);
      setError(err.message || 'Email atau password tidak valid');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleParentRegister(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await apiRequest(apiBase, '/admissions/register', {
        method: 'POST',
        body: JSON.stringify(parentForm),
      });
      setSuccess('Akun orang tua berhasil dibuat. Silakan login pada form utama.');
      setShowParentRegister(false);
      setEmail(parentForm.email);
      setParentForm({ full_name: '', email: '', password: '' });
    } catch (err) {
      setError(err.message || 'Gagal membuat akun orang tua');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStaffLogout() {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(apiBase, '/auth/logout', { method: 'POST' });
      setStaffUser(null);
      setContext('none');
      setSuccess('Logout berhasil.');
    } catch (err) {
      setError(err.message || 'Gagal logout');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="access-gate-page">
        <section className="access-gate-card">
          <h2>Unified Access Portal</h2>
          <p>Memuat sesi login...</p>
        </section>
      </main>
    );
  }

  if (context === 'super_admin') {
    return (
      <SuperAdminPanel
        apiBase={apiBase}
        currentUser={staffUser}
        onLogin={(user) => {
          setStaffUser(user);
          setContext(resolveStaffContext(user));
        }}
        onLogout={() => {
          setStaffUser(null);
          setContext('none');
        }}
      />
    );
  }

  if (context === 'admissions_admin') {
    return (
      <section className="super-admin-section">
        <div className="super-admin-shell super-admin-shell-dashboard">
          <div className="super-admin-headline">
            <div>
              <p className="super-admin-kicker">Administrator</p>
              <h2>Panel PPDB</h2>
              <p className="super-admin-subtitle">{staffUser?.full_name} ({staffUser?.email})</p>
            </div>
            <button type="button" className="super-admin-logout" onClick={handleStaffLogout} disabled={submitting}>
              Logout
            </button>
          </div>
          <AdmissionsAdminPanel apiBase={apiBase} />
        </div>
      </section>
    );
  }

  if (context === 'cms_admin') {
    return (
      <section className="super-admin-section">
        <div className="super-admin-shell super-admin-shell-dashboard">
          <div className="super-admin-headline">
            <div>
              <p className="super-admin-kicker">Administrator</p>
              <h2>Panel CMS</h2>
              <p className="super-admin-subtitle">{staffUser?.full_name} ({staffUser?.email})</p>
            </div>
            <button type="button" className="super-admin-logout" onClick={handleStaffLogout} disabled={submitting}>
              Logout
            </button>
          </div>
          <CmsWorkflowPanel apiBase={apiBase} />
        </div>
      </section>
    );
  }

  if (context === 'parent') {
    return <AdmissionsPortal apiBase={apiBase} />;
  }

  return (
    <main className="access-gate-page">
      <section className="access-gate-card">
        <h2>Unified Access Portal</h2>
        <p>
          Satu pintu login untuk staff dan orang tua. Sistem akan otomatis menampilkan panel sesuai akun.
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-info">{success}</div>}

        <form className="access-gate-form" onSubmit={handleUnifiedLogin}>
          <label>
            Email
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button type="submit" disabled={submitting}>
            {submitting ? 'Processing...' : 'Login'}
          </button>
        </form>

        <div className="access-gate-hints">
          <p>Routing otomatis setelah login:</p>
          <ul>
            <li>Role super_admin {'->'} Super Admin Dashboard</li>
            <li>Role admissions {'->'} Panel PPDB</li>
            <li>Role admin/editor/approver {'->'} Panel CMS</li>
            <li>Akun orang tua {'->'} Portal Pendaftaran</li>
          </ul>
        </div>

        <button
          type="button"
          className="access-gate-secondary"
          onClick={() => setShowParentRegister((prev) => !prev)}
        >
          {showParentRegister ? 'Tutup Form Akun Orang Tua' : 'Buat Akun Orang Tua'}
        </button>

        {showParentRegister && (
          <form className="access-gate-form" onSubmit={handleParentRegister}>
            <label>
              Nama Lengkap
              <input
                type="text"
                value={parentForm.full_name}
                onChange={(event) => setParentForm((prev) => ({ ...prev, full_name: event.target.value }))}
                required
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={parentForm.email}
                onChange={(event) => setParentForm((prev) => ({ ...prev, email: event.target.value }))}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                minLength={8}
                value={parentForm.password}
                onChange={(event) => setParentForm((prev) => ({ ...prev, password: event.target.value }))}
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Create Parent Account'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

export default UnifiedAccessPortal;
